import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/mui";
import {
    Alert,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import {
    AttachmentGallery,
    DetailSection,
    FieldGrid,
    FieldItem,
    RelatedLink,
} from "components/detail";
import { StatusChip } from "components/list";

import { IScheduleDetail } from "./interfaces";
import { completeMeta, acceptMeta, resolveStatusMeta } from "./components/scheduleStatus";
import {
    joinParts,
    scheduleAggregates,
    getAttachmentUrls,
} from "./components/scheduleDetail";
import styles from "./show.module.css";

export const ScheduleShow = () => {
    const { queryResult } = useShow<IScheduleDetail>();
    const { data, isLoading, isError } = queryResult;
    const record = data?.data;

    const navigate = useNavigate();

    // 값이 없으면 "—"로 대체하는 표시용 포매터
    const fmt = (value: unknown) => joinParts([value]) ?? "—";

    const completeStatus = resolveStatusMeta(completeMeta, record?.complete);
    const acceptStatus = resolveStatusMeta(acceptMeta, record?.accept);

    return (
        <Show
            isLoading={isLoading}
            title={
                <Typography variant="h5">
                    방문일정 상세{record?.id ? ` · ${record.id}` : ""}
                </Typography>
            }
            headerButtons={({ defaultButtons }) => (
                <>
                    {defaultButtons}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            navigate("/schedules/files/" + record?.id);
                        }}
                    >
                        첨부파일 수정
                    </Button>
                </>
            )}
        >
            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    방문일정을 불러오지 못했습니다.
                </Alert>
            )}

            <Stack spacing={2}>
                <DetailSection title="기본 정보">
                    <FieldGrid columns={2}>
                        <FieldItem
                            label="현장요원"
                            value={
                                record?.agent?.id && record?.agent?.name ? (
                                    <RelatedLink
                                        to={`/agents/show/${record.agent.id}`}
                                        label={record.agent.name}
                                    />
                                ) : (
                                    record?.agent?.name
                                )
                            }
                        />
                        <FieldItem
                            label="시설"
                            value={
                                record?.center?.id && record?.center?.name ? (
                                    <RelatedLink
                                        to={`/centers/show/${record.center.id}`}
                                        label={record.center.name}
                                    />
                                ) : (
                                    record?.center?.name
                                )
                            }
                        />
                        <FieldItem label="접수일" value={record?.receipt_date} />
                        <FieldItem
                            label="일정 상태"
                            value={
                                <StatusChip
                                    label={completeStatus.label}
                                    color={completeStatus.color}
                                />
                            }
                        />
                        <FieldItem
                            label="현장요원 수락"
                            value={
                                <StatusChip
                                    label={acceptStatus.label}
                                    color={acceptStatus.color}
                                />
                            }
                        />
                        <FieldItem
                            label="서류 발송"
                            value={record?.documentSent}
                        />
                        <FieldItem
                            label="최근 통화 상태"
                            span={2}
                            value={joinParts([record?.call_check, record?.call_check_info])}
                        />
                        <FieldItem
                            label="변경사항"
                            span={2}
                            multiline
                            value={record?.modified_info}
                        />
                    </FieldGrid>
                </DetailSection>

                <DetailSection title="방문 일정">
                    <FieldGrid columns={2}>
                        <FieldItem
                            label="예정 방문일시"
                            value={joinParts([record?.visitDate, record?.visitTime])}
                        />
                        <FieldItem
                            label="실제 방문일시"
                            value={joinParts([record?.visitDateFinal, record?.visitTimeFinal])}
                        />
                    </FieldGrid>
                </DetailSection>

                <DetailSection
                    title="방문 결과 집계"
                    description="예상 대비 최종 확정 인원"
                >
                    <TableContainer>
                        <Table
                            size="small"
                            className={styles.aggregateTable}
                            sx={{
                                "& td, & th": { borderColor: "divider" },
                                "& td": { fontVariantNumeric: "tabular-nums" },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>항목</TableCell>
                                    <TableCell align="right">예상</TableCell>
                                    <TableCell align="right">최종</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduleAggregates(record).map((row) => (
                                    <TableRow key={row.label}>
                                        <TableCell>{row.label}</TableCell>
                                        <TableCell align="right">{fmt(row.estimated)}</TableCell>
                                        <TableCell align="right">{fmt(row.final)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <FieldGrid columns={2} sx={{ mt: 2 }}>
                        <FieldItem label="결석 최종" value={record?.absenceFinal} />
                        <FieldItem
                            label="결석관련 메모"
                            span={2}
                            multiline
                            value={record?.absenceComment}
                        />
                    </FieldGrid>
                </DetailSection>

                <DetailSection title="특이사항 · 메모">
                    <FieldGrid columns={1}>
                        <FieldItem label="시설 특이사항" multiline value={record?.center_etc} />
                        <FieldItem label="현장요원 특이사항" multiline value={record?.agent_etc} />
                        <FieldItem label="스케줄 특이사항" multiline value={record?.total_etc} />
                        <FieldItem label="관리자 메모" multiline value={record?.memo} />
                        <FieldItem label="일정 취소 사유" multiline value={record?.cancel_comment} />
                        <FieldItem label="지각 사유" multiline value={record?.late_comment} />
                    </FieldGrid>
                </DetailSection>

                <DetailSection
                    title="첨부 파일"
                    action={
                        record?.id ? (
                            <RelatedLink
                                to={`/schedules/files/${record.id}`}
                                label="첨부파일 관리"
                            />
                        ) : undefined
                    }
                >
                    <AttachmentGallery items={getAttachmentUrls(record)} />
                </DetailSection>
            </Stack>
        </Show>
    );
};
